import * as React from "react";
import { useContext, useState, useCallback } from "react";

import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { IsoLocale, Locales, UsaIsoLocale, getIsoLocaleFromCountry, getLocalesFromIsoLocale } from "@simplyk/common";
import { changeLanguage } from "i18next";
import { useRouter } from "next/router";
import { I18nextProvider } from "react-i18next";

import i18n from "../config/i18n";

import { useCurrentUserContext } from "./CurrentUserContext";

interface LocaleContextProps {
  isoLocale: IsoLocale;
  setIsoLocale: (locale: IsoLocale) => void;
  locale: Locales;
}

export const LocaleContext = React.createContext<LocaleContextProps>({
  isoLocale: UsaIsoLocale.EN,
  setIsoLocale: () => {},
  locale: Locales.EN,
});

interface LocaleProviderProps {
  children: React.ReactElement<unknown>;
  skipUserLocaleRedirection?: boolean;
}

export const useLocaleContext = (): LocaleContextProps => {
  return useContext(LocaleContext);
};

export const LocaleProvider: React.FunctionComponent<React.PropsWithChildren<LocaleProviderProps>> = ({ children }) => {
  const router = useRouter();
  const { locale: routerLocale } = router as { locale: IsoLocale };
  const { currentUser, organization } = useCurrentUserContext();

  const initialIsoLocale = (i18n?.language || routerLocale || UsaIsoLocale.EN) as IsoLocale;
  const [isoLocale, setIsoLocaleState] = useState<IsoLocale>(initialIsoLocale); // The state is used to store the isoLocale, and re-render the page when it changes
  const locale = getLocalesFromIsoLocale(isoLocale);

  const handleSetIsoLocale = useCallback((newLocale: IsoLocale) => {
    setIsoLocaleState(newLocale);
  }, []);

  // Set the isoLocale based on the currentUser.locale and organization.country
  React.useEffect(() => {
    if (currentUser?.locale) {
      const userIsoLocale = getIsoLocaleFromCountry(organization?.country, currentUser.locale);
      if (userIsoLocale && i18n.language !== userIsoLocale) {
        handleSetIsoLocale(userIsoLocale);
      }
    }
  }, [currentUser?.locale, handleSetIsoLocale, organization?.country]);

  if (i18n.language !== isoLocale) {
    changeLanguage(isoLocale);
  }

  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={{ isoLocale, setIsoLocale: handleSetIsoLocale, locale }}>
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
          {children}
        </LocalizationProvider>
      </LocaleContext.Provider>
    </I18nextProvider>
  );
};
